/**
 * Fast Entry (Asana 1219423341660540): the pure rules behind the Add search's
 * set lock and collector-number entry. The matching itself runs on the backend
 * (/cards/search-light: `filter` precedence exact set code, else digit prefix,
 * else set-name word start; `collector_number` exact), so the frontend only
 * decides which params to send.
 */

/** "264", "264s", "264★": a collector number typed into the main search while a set is locked. */
export const COLLECTOR_NUMBER_RE = /^\d+[a-z★]*$/;

export const isCollectorNumberEntry = (text: string): boolean => COLLECTOR_NUMBER_RE.test(text.trim());

/** Printings rendered before "Show all N printings", in both List and Image mode. */
export const IMAGE_RESULTS_LIMIT = 24;

/** How long the add confirmation stays before it fades; the next add replaces it. */
export const TOAST_DURATION_MS = 5000;

/** The trader's List / Image choice, per browser. */
export const ADD_SEARCH_VIEW_STORAGE_KEY = 'tm.myCards.addSearchView';
export type AddSearchView = 'list' | 'image';

export interface AddSearchArgsInput {
  /** The debounced main search text. */
  search: string;
  /** The debounced printings filter text (unlocked). */
  filterText: string;
  /** The locked filter text, or null. Survives adds and search changes. */
  lockedFilter: string | null;
  /** Shift+Enter / "Search all sets": drop the lock for this one search. */
  searchAll: boolean;
}

export interface AddSearchArgs {
  name?: string;
  collector_number?: string;
  filter?: string;
}

/**
 * The /cards/search-light args for the Specific Set search. While a set is
 * locked, a collector number in the main search looks that number up inside the
 * lock instead of searching names; "search all" sends no filter for this search
 * and leaves the lock in place for the next one.
 */
export const buildAddSearchArgs = ({ search, filterText, lockedFilter, searchAll }: AddSearchArgsInput): AddSearchArgs => {
  const text = search.trim();
  const filter = searchAll ? undefined : (lockedFilter ?? (filterText.trim() || undefined));
  if (lockedFilter && !searchAll && isCollectorNumberEntry(text)) {
    return { collector_number: text, filter };
  }
  return { name: text || undefined, filter };
};

/** Nothing to ask the server for. */
export const addSearchIsIdle = (args: AddSearchArgs): boolean => !args.name && !args.collector_number;

export const readStoredAddSearchView = (): AddSearchView => {
  try {
    return localStorage.getItem(ADD_SEARCH_VIEW_STORAGE_KEY) === 'image' ? 'image' : 'list';
  } catch {
    return 'list';
  }
};

export const storeAddSearchView = (view: AddSearchView): void => {
  try {
    localStorage.setItem(ADD_SEARCH_VIEW_STORAGE_KEY, view);
  } catch {
    // Storage unavailable: the choice lasts for the session only.
  }
};

/**
 * Dropdown height (Asana 1219947685097226): the results overlay fills the room
 * between its top edge and the bottom of the viewport, minus a margin, and never
 * drops below the minimum; when there is less room than that the page scrolls.
 * Applied as max-height, so a short result list keeps the dropdown short.
 */
export const DROPDOWN_VIEWPORT_MARGIN_PX = 16;
export const DROPDOWN_MIN_HEIGHT_PX = 320;
/** Under this width the viewport is measured with window.visualViewport (on-screen keyboard). */
export const DROPDOWN_MOBILE_MAX_WIDTH_PX = 640;

export const computeDropdownMaxHeight = (viewportHeight: number, overlayTop: number): number =>
  Math.max(DROPDOWN_MIN_HEIGHT_PX, viewportHeight - overlayTop - DROPDOWN_VIEWPORT_MARGIN_PX);
